import React from 'react';
import { EyeIcons, MorevertIcons, UserPenIcons } from '../../icons/icons';
import { DataTable, NameEmailAvatar, TableColumn } from '../../ui';
import {
  ContextMenu,
  MenuItemType,
} from '../../ui/components/Common/ContextMenu/ContextMenu';
import { ContextMenuButton } from '../../ui/components/Common/ContextMenu/ContextMenuButton';
import { TrashIcon } from '../../ui/icons/icons';
import { TooltipWrapper } from '../job_openings/view/Functions';
import { OrganizationSubscription } from './types';

interface OrganizationsTableProps {
  data: OrganizationSubscription[];
  onEdit: (org: OrganizationSubscription) => void;
  onDelete: (org: OrganizationSubscription) => void;
  onView: (org: OrganizationSubscription) => void;
  onRowClick: (org: OrganizationSubscription) => void;
}

const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  data,
  onEdit,
  onDelete,
  onView,
  onRowClick,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [selectedOrg, setSelectedOrg] =
    React.useState<OrganizationSubscription | null>(null);

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: unknown,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedOrg(row as OrganizationSubscription);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedOrg(null);
  };

  const menuItems: MenuItemType<OrganizationSubscription>[] = [
    {
      label: 'View Details',
      icon: <EyeIcons className='w-4 h-4' />,
      permission: [],
      onClick: (row) => onView(row),
    },
    {
      label: 'Edit Organization',
      icon: <UserPenIcons className='w-4 h-4' />,
      permission: [],
      onClick: (row) => onEdit(row),
    },
    {
      label: 'Delete',
      icon: <TrashIcon className='w-4 h-4 text-rose-500' />,
      permission: [],
      onClick: (row) => onDelete(row),
    },
  ];

  const columns: TableColumn<OrganizationSubscription>[] = [
    {
      key: 'organization',
      label: 'Organization',
      width: '240px',
      align: 'left' as const,
      render: (row) => (
        <div className='flex items-center gap-3'>
          <NameEmailAvatar
              avatar={row.organization.companyLogo ?? ''}
              name={row.organization.organizationName}
              email={row.organization.primaryDomain}
          />
        </div>
      ),
    },
    {
      key: 'organization',
      label: 'Primary Domain',
      width: '180px',
      align: 'left' as const,
      render: (row) => <TooltipWrapper text={row.organization.primaryDomain} />,
    },
    {
      key: 'subscription',
      label: 'Company Size',
      align: 'left' as const,
      width: '140px',
      render: (row) => <TooltipWrapper text={row.subscription.companySize} />,
    },
    {
      key: 'subscription',
      label: 'Subscription',
      align: 'left' as const,
      width: '140px',
      render: (row) => {
        const status = row.subscription.candidateFeature?.status ?? 'unknown';
        const colors: Record<string, string> = {
          trialing: 'bg-amber-50 text-amber-600 border-amber-100',
          active: 'bg-emerald-50 text-emerald-600 border-emerald-100',
          canceled: 'bg-rose-50 text-rose-500 border-rose-100',
          past_due: 'bg-orange-50 text-orange-600 border-orange-100',
        };
        return (
          <div className='flex justify-start text-left'>
            <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                  colors[status] ?? 'bg-slate-50 text-slate-500 border-slate-100'
                }`}
            >
              { status }
            </span>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      align: 'right',
      render: (row) => (
        <div className='flex justify-end pr-2'>
          <ContextMenuButton
              row={row}
              onOpenMenu={handleOpenMenu}
              onCloseMenu={handleCloseMenu}
              anchorEl={anchorEl}
          />
        </div>
      ),
    },
  ];

  return (
    <div className='bg-white overflow-hidden border border-slate-100 shadow-sm'>
      <DataTable
          columns={columns}
          data={data}
          rowsPerPage={10}
          currentPage={1}
          totalRecords={data.length}
          totalPages={1}
          onPageChange={() => {}}
          onClickRow={onRowClick}
      />
      { anchorEl && selectedOrg && (
        <ContextMenu
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            row={selectedOrg}
            menuItems={menuItems}
        />
      ) }
    </div>
  );
};

export default OrganizationsTable;
